import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getLocalizedApiMessage } from "../../../utils/apiMessageHelper";
import {
  createTeamRequest,
  getTeams,
  removeTeam,
  updateTeamRequest,
} from "../../../services/api/teams/teamsApi";

const rejectApiResponse = (responseData, fallbackMessage) => {
  if (responseData?.success === false) {
    return responseData.message ?? fallbackMessage;
  }
  return null;
};

export const fetchTeams = createAsyncThunk(
  "teams/fetchTeams",
  async (dateRange = {}, { rejectWithValue }) => {
    try {
      const response = await getTeams(dateRange);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch teams");
    }
  },
);

export const deleteTeam = createAsyncThunk(
  "teams/deleteTeam",
  async (teamId, { rejectWithValue }) => {
    try {
      await removeTeam(teamId);
      return teamId;
    } catch (error) {
      return rejectWithValue("Failed to delete team");
    }
  },
);

export const addTeam = createAsyncThunk(
  "teams/addTeam",
  async (teamData, { rejectWithValue }) => {
    try {
      const response = await createTeamRequest(teamData);

      const apiError = rejectApiResponse(response.data, "Failed to add team");
      if (apiError) {
        return rejectWithValue(apiError);
      }

      return {
        team: response.data.data,
        message: response.data?.message,
      };
    } catch (error) {
      return rejectWithValue(getLocalizedApiMessage(error, "Failed to add team"));
    }
  },
);

export const updateTeam = createAsyncThunk(
  "teams/updateTeam",
  async ({ teamId, teamData }, { rejectWithValue }) => {
    try {
      const response = await updateTeamRequest(teamId, teamData);

      const apiError = rejectApiResponse(response.data, "Failed to update team");
      if (apiError) {
        return rejectWithValue(apiError);
      }

      return {
        team: response.data.data,
        message: response.data?.message,
      };
    } catch (error) {
      return rejectWithValue(
        getLocalizedApiMessage(error, "Failed to update team"),
      );
    }
  },
);

const teamsSlice = createSlice({
  name: "teams",
  initialState: {
    teamData: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTeams.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTeams.fulfilled, (state, action) => {
        state.loading = false;
        state.teamData = action.payload;
      })
      .addCase(fetchTeams.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteTeam.fulfilled, (state, action) => {
        state.teamData = state.teamData.filter(
          (team) => team.id !== action.payload,
        );
      })
      .addCase(deleteTeam.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(addTeam.fulfilled, (state, action) => {
        if (action.payload?.team) {
          state.teamData.push(action.payload.team);
        }
      })
      .addCase(addTeam.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updateTeam.fulfilled, (state, action) => {
        const team = action.payload?.team;
        if (!team) return;

        const index = state.teamData.findIndex((item) => item.id === team.id);
        if (index !== -1) {
          state.teamData[index] = team;
        }
      })
      .addCase(updateTeam.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export default teamsSlice.reducer;
